// ----------------------------------------------------------------------------
// Domain types (mirror supabase/migrations)
// ----------------------------------------------------------------------------

export type MemberRole = 'owner' | 'admin' | 'editor';
export type ActionType = 'announcement' | 'sermon_notes' | 'poll' | 'offering' | 'form' | 'question' | 'link';

export interface LandingLink {
  label: string;
  url: string;
}

export interface PlanningCenterConfig {
  app_id?: string;
  secret?: string;
  church_center_subdomain?: string; // e.g. "gracecommunity" for gracecommunity.churchcenter.com
}

export interface PlanningCenterItem {
  id: string;
  type: 'form' | 'signup' | 'event';
  title: string;
  description?: string;
  url: string;
}

export interface AppDeeplink {
  id: string;
  label: string;
  ios_url: string;
  android_url: string;
  created_at?: string;
}

export type SlideCtaStyle = 'button' | 'link' | 'icon';
export type SlideCtaActionType = 'url' | 'mailto' | 'deeplink' | 'pco';

export interface SlideImageBlock {
  enabled: boolean;
  url?: string;
  is_full_image?: boolean;
}

export interface SlideTextBlock {
  enabled: boolean;
  title?: string;
  subtitle?: string;
  body?: string;
}

export interface SlideCtaBlock {
  enabled: boolean;
  style: SlideCtaStyle;
  icon?: string;
  label: string;
  action_type: SlideCtaActionType;
  target_url?: string;
  deeplink_id?: string;
  mailto_email?: string;
  mailto_subject?: string;
}

export interface SlideDefinition {
  id: string;
  name: string;
  image: SlideImageBlock;
  text: SlideTextBlock;
  cta: SlideCtaBlock;
  created_at?: string;
  updated_at?: string;
}

export interface SermonSlideImageBlock {
  enabled: boolean;
  url?: string;
  is_full_image?: boolean;
}

export interface SermonSlideTextBlock {
  enabled: boolean;
  show_number?: boolean; // Whether to display a slide/point number badge in the frontend
  slide_number?: string; // Custom number or label, e.g. "1", "A", "Part 2"
  title?: string; // Main Point (optional)
  verse_reference?: string; // Bible Verse (optional)
  verse_translation?: string; // Bible translation (e.g. "web", "kjv")
  body?: string; // Body Text RTE (optional)
}

export interface SermonSlideDefinition {
  id: string;
  name?: string;
  image: SermonSlideImageBlock;
  text: SermonSlideTextBlock;
  created_at?: string;
  updated_at?: string;
}

export interface SermonRecord {
  id: string;
  title: string;
  speaker?: string;
  date?: string; // YYYY-MM-DD
  main_verse?: string;
  description?: string;
  slides: SermonSlide[];
  allow_personal_notes?: boolean;
  service_id?: string | null;
  created_at?: string;
  updated_at?: string;
}

export type WelcomeModalIcon = 'sparkles' | 'smartphone' | 'book' | 'check' | 'bell' | 'heart' | 'info';

export interface WelcomeModalHighlight {
  id: string;
  icon?: WelcomeModalIcon;
  title: string;
  description: string;
}

export interface WelcomeModalConfig {
  enabled?: boolean;
  title?: string;
  subtitle?: string;
  button_label?: string;
  highlights?: WelcomeModalHighlight[];
  version?: number;
}

export interface LandingConfig {
  welcome_title?: string;
  welcome_message?: string;
  hero_image_url?: string;
  theme?: 'light' | 'dark' | 'brand';
  show_service_name?: boolean;
  show_next_service?: boolean;
  idle_title?: string;
  idle_message?: string;
  footer_text?: string;
  links?: LandingLink[];
  planning_center?: PlanningCenterConfig;
  slides?: SlideDefinition[];
  sermons?: SermonRecord[];
  app_deeplinks?: AppDeeplink[];
  welcome_modal?: WelcomeModalConfig;
}

export interface Church {
  id: string;
  slug: string;
  name: string;
  timezone: string;
  logo_url: string | null;
  primary_color: string;
  accent_color: string;
  landing: LandingConfig;
  created_at: string;
  updated_at: string;
}

export interface Membership {
  church_id: string;
  role: MemberRole;
  churches: Church;
}

export interface Service {
  id: string;
  church_id: string;
  name: string;
  slug: string;
  description: string | null;
  lead_minutes: number;
  trail_minutes: number;
  live_action_id: string | null;
  live_action_until: string | null;
  is_active: boolean;
  sort_order: number;
  service_times?: ServiceTime[];
}

export interface ServiceTime {
  id: string;
  service_id: string;
  day_of_week: number | null;
  specific_date: string | null;
  start_time: string; // HH:MM:SS
  end_time: string;
  label: string | null;
  is_active: boolean;
}

// ---- Action content per type ----------------------------------------------
export interface AnnouncementSlide {
  id: string;
  title?: string;
  subtitle?: string;
  body?: string; // markdown
  image_url?: string;
  is_full_image?: boolean;
  cta_label?: string;
  cta_url?: string;
  cta_style?: SlideCtaStyle;
  cta_icon?: string;
  cta_action_type?: SlideCtaActionType;
  deeplink_id?: string;
  deeplink_ios_url?: string;
  deeplink_android_url?: string;
  slide_id?: string; // Link to slide in church library
}

export interface AnnouncementContent {
  // Slideshow support (each slide has its own image, text, and CTA)
  slides?: AnnouncementSlide[];
  selected_slide_ids?: string[];
  auto_advance_seconds?: number;

  // Single-slide legacy fields (kept for backwards compatibility)
  body?: string; // markdown
  image_url?: string;
  cta_label?: string;
  cta_url?: string;
}

export interface SermonSlide {
  id: string;
  show_number?: boolean; // Whether to display a slide/point number in the frontend (defaults to true if slide_number is set or fallback)
  slide_number?: string; // Custom number/label, e.g. "1", "Point 1", "I", "Intro"
  title?: string; // Point or headline, e.g. "Present struggles are temporary"
  verse_reference?: string; // e.g. "Romans 8:18" or "John 3:16"
  verse_translation?: string; // e.g. "web", "kjv", "bbe"
  verse_text?: string; // Optional manual override or cached verse text
  body?: string; // Optional explanatory notes/bullet points (markdown)
  image_url?: string; // Background / feature image for the slide
  is_full_image?: boolean;
  slide_id?: string;
}

export interface SermonNotesContent {
  speaker?: string;
  main_verse?: string; // Primary sermon scripture, e.g. "Romans 8:18–28"
  slides?: SermonSlide[]; // Sermon slides displayed sequentially one under the other
  allow_personal_notes?: boolean;
  sermon_id?: string; // Optional link to SermonRecord in library

  // Backwards compatibility for older single markdown body
  scripture?: string;
  body?: string; // markdown
}

export interface PollOption {
  id: string;
  label: string;
}
export interface PollContent {
  question?: string;
  options?: PollOption[];
  show_results?: boolean;
  allow_change?: boolean;
  closed?: boolean;
}

export interface OfferingMethod {
  id: string;
  label: string;
  description?: string;
  url?: string;
  account_holder?: string; // Beneficiary / Account holder name
  iban?: string; // IBAN for bank transfer
  bic?: string; // Optional BIC / SWIFT code
  reference?: string; // Payment reference / purpose text
  details?: string; // Additional / legacy details or notes
}
export interface OfferingContent {
  message?: string;
  methods?: OfferingMethod[];
}

export type FormFieldType = 'text' | 'textarea' | 'email' | 'phone';
export interface FormField {
  id: string;
  label: string;
  type: FormFieldType;
  required?: boolean;
  placeholder?: string;
}
export interface FormContent {
  intro?: string;
  fields?: FormField[];
  submit_label?: string;
  success_message?: string;
}

export interface QuestionContent {
  intro?: string;
  allow_anonymous?: boolean;
  require_name?: boolean;
  submit_label?: string;
  success_message?: string;
}

export interface LinkContent {
  url?: string;
  label?: string;
  description?: string;
  image_url?: string;
}

export type ActionContent =
  | AnnouncementContent
  | SermonNotesContent
  | PollContent
  | OfferingContent
  | FormContent
  | QuestionContent
  | LinkContent;

export interface Action {
  id: string;
  church_id: string;
  service_id: string | null;
  type: ActionType;
  title: string;
  content: Record<string, unknown>;
  start_offset_minutes: number | null;
  end_offset_minutes: number | null;
  priority: number;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface ActionResponse {
  id: string;
  action_id: string;
  church_id: string;
  payload: Record<string, string>;
  is_archived: boolean;
  created_at: string;
}

export interface Member {
  user_id: string;
  email: string | null;
  full_name: string | null;
  role: MemberRole;
  created_at: string;
}

export interface Invitation {
  id: string;
  church_id: string;
  email: string;
  role: MemberRole;
  created_at: string;
}

// ---- Public live page (get_live_page RPC) ---------------------------------
export interface LiveAction {
  id: string;
  type: ActionType;
  title: string;
  content: Record<string, unknown>;
  pinned: boolean;
  visible_until: string | null;
}

export interface LivePage {
  church: Pick<Church, 'id' | 'slug' | 'name' | 'timezone' | 'logo_url' | 'primary_color' | 'accent_color' | 'landing'>;
  live: boolean;
  service: { id: string; name: string; slug: string; starts_at: string; ends_at: string } | null;
  actions: LiveAction[];
  next_service: { name: string; slug: string; starts_at: string; label: string | null } | null;
  server_time: string;
}

export interface PollResults {
  hidden: boolean;
  total?: number;
  counts?: Record<string, number>;
}

// ---- Metadata --------------------------------------------------------------
export const ACTION_TYPES: { type: ActionType; label: string; description: string }[] = [
  { type: 'announcement', label: 'Announcement', description: 'News, events and highlights with an optional button' },
  { type: 'sermon_notes', label: 'Sermon notes', description: 'Outline, scripture and personal note-taking' },
  { type: 'poll', label: 'Live poll', description: 'Ask a question and show results live' },
  { type: 'question', label: 'Ask a question', description: 'Collect attendee questions for sermon Q&A or team review' },
  { type: 'offering', label: 'Offering', description: 'Giving options: online links, bank transfer, …' },
  { type: 'form', label: 'Form', description: 'Prayer requests, connect cards, feedback' },
  { type: 'link', label: 'Link', description: 'Send people to a page, livestream or sign-up' },
];

export const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
